/**
 * Locations API functions
 * For managing specific, reusable locations
 */

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";

// =============================================================================
// Types
// =============================================================================

export interface LocationSummary {
  id: number;
  name: string;
  description: string | null;
  city: string | null;
  county: string | null;
  country: string | null;
  geo_lat: number | null;
  geo_lng: number | null;
  media_count: number;
  person_count: number;
}

export interface TaggedMediaInfo {
  id: number;
  filename: string;
  original_filename: string;
  content_type: string;
  caption: string | null;
}

export interface TaggedPersonInfo {
  id: number;
  display_name: string;
  tree_id: number | null;
}

export interface TaggedConnectedPersonInfo {
  id: number;
  display_name: string;
}

export interface LocationDetail {
  id: number;
  name: string;
  description: string | null;
  address: string | null;
  city: string | null;
  county: string | null;
  country: string | null;
  postal_code: string | null;
  geo_lat: number | null;
  geo_lng: number | null;
  display_address: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  media_items: TaggedMediaInfo[];
  persons: TaggedPersonInfo[];
  connected_persons: TaggedConnectedPersonInfo[];
}

export interface LocationCreate {
  name: string;
  description?: string;
  address?: string;
  city?: string;
  county?: string;
  country?: string;
  postal_code?: string;
  geo_lat?: number;
  geo_lng?: number;
}

export interface LocationUpdate {
  name?: string;
  description?: string;
  address?: string;
  city?: string;
  county?: string;
  country?: string;
  postal_code?: string;
  geo_lat?: number;
  geo_lng?: number;
}

export interface TagRequest {
  media_id?: number;
  person_id?: number;
  connected_person_id?: number;
}

// =============================================================================
// CRUD Operations
// =============================================================================

/**
 * List all locations with optional filtering
 */
export async function listLocations(
  token: string,
  options?: {
    search?: string;
    city?: string;
    country?: string;
    limit?: number;
    offset?: number;
    vaultId?: number;
  }
): Promise<LocationSummary[]> {
  const params = new URLSearchParams();
  if (options?.search) params.append("search", options.search);
  if (options?.city) params.append("city", options.city);
  if (options?.country) params.append("country", options.country);
  if (options?.limit) params.append("limit", options.limit.toString());
  if (options?.offset) params.append("offset", options.offset.toString());
  if (options?.vaultId) params.append("vault_id", options.vaultId.toString());

  const queryString = params.toString();
  const url = `${API_URL}/locations${queryString ? `?${queryString}` : ""}`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch locations: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get a specific location with all tagged items
 */
export async function getLocation(
  token: string,
  locationId: number
): Promise<LocationDetail> {
  const response = await fetch(`${API_URL}/locations/${locationId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch location: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create a new location
 */
export async function createLocation(
  token: string,
  data: LocationCreate
): Promise<LocationSummary> {
  const response = await fetch(`${API_URL}/locations`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to create location: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update a location
 */
export async function updateLocation(
  token: string,
  locationId: number,
  data: LocationUpdate
): Promise<LocationSummary> {
  const response = await fetch(`${API_URL}/locations/${locationId}`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to update location: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete a location
 */
export async function deleteLocation(
  token: string,
  locationId: number
): Promise<void> {
  const response = await fetch(`${API_URL}/locations/${locationId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to delete location: ${response.statusText}`);
  }
}

// =============================================================================
// Tagging Operations
// =============================================================================

/**
 * Tag a media, person, or connected person to a location
 */
export async function tagItemToLocation(
  token: string,
  locationId: number,
  tagData: TagRequest
): Promise<{ message: string }> {
  const response = await fetch(`${API_URL}/locations/${locationId}/tag`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(tagData),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to tag item: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Remove a tag from a location
 */
export async function untagItemFromLocation(
  token: string,
  locationId: number,
  tagData: TagRequest
): Promise<void> {
  const params = new URLSearchParams();
  if (tagData.media_id) params.append("media_id", tagData.media_id.toString());
  if (tagData.person_id) params.append("person_id", tagData.person_id.toString());
  if (tagData.connected_person_id) {
    params.append("connected_person_id", tagData.connected_person_id.toString());
  }

  const response = await fetch(
    `${API_URL}/locations/${locationId}/tag?${params.toString()}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to untag item: ${response.statusText}`);
  }
}

// =============================================================================
// Lookup Operations
// =============================================================================

/**
 * Get locations tagged to a specific media item
 */
export async function getLocationsForMedia(
  token: string,
  mediaId: number
): Promise<LocationSummary[]> {
  const response = await fetch(`${API_URL}/locations/by-media/${mediaId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch locations for media: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get locations tagged to a specific person
 */
export async function getLocationsForPerson(
  token: string,
  personId: number
): Promise<LocationSummary[]> {
  const response = await fetch(`${API_URL}/locations/by-person/${personId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch locations for person: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get locations tagged to a specific connected person
 */
export async function getLocationsForConnectedPerson(
  token: string,
  connectedPersonId: number
): Promise<LocationSummary[]> {
  const response = await fetch(`${API_URL}/locations/by-connected-person/${connectedPersonId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch locations for connected person: ${response.statusText}`);
  }

  return response.json();
}
